import { motion } from "framer-motion";
import { Search, CreditCard, BarChart3, Truck, Check } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Services } from "../components/home/Services";
import { SendLink } from "../components/home/SendLink";
import { Trust } from "../components/home/Trust";
import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";
import { ASSETS } from "../lib/site";

const meta = {
  source: { Icon: Search, red: false, img: ASSETS.negotiation },
  pay: { Icon: CreditCard, red: true, img: ASSETS.boxes },
  finance: { Icon: BarChart3, red: false, img: ASSETS.businessMeeting },
  move: { Icon: Truck, red: true, img: ASSETS.warehouse },
};

export default function ServicesPage() {
  const { t } = useLang();
  return (
    <main data-testid="services-page">
      <PageHero kicker={t.nav.services} title={t.pages.services.title} sub={t.pages.services.sub} testId="services-page-hero" />
      <Services />

      {/* How it works */}
      <section className="bg-sinovar-soft py-16 md:py-24" data-testid="services-how">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-sinovar-navy">{t.services.how.title}</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.services.how.steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-2xl border border-black/[0.06] bg-white p-8"
              >
                <span className="font-display text-4xl font-bold text-sinovar-blue/15">{s.n}</span>
                <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-sinovar-navy">{s.t}</h3>
                <p className="mt-2 text-[15px] text-sinovar-navy/60 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Per-pillar detail rows */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8 space-y-16 md:space-y-24">
          {t.services.items.map((s, i) => {
            const { Icon, red, img } = meta[s.key];
            const flip = i % 2 === 1;
            return (
              <div key={s.key} id={s.key} className="scroll-mt-28 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                <Reveal className={flip ? "lg:order-2" : ""}>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${red ? "bg-sinovar-red/10 text-sinovar-red" : "bg-sinovar-blue/10 text-sinovar-blue"}`}>
                    <Icon className="h-6 w-6" strokeWidth={1.6} />
                  </div>
                  <div className="mt-5 text-xs font-bold uppercase tracking-[0.12em] text-sinovar-navy/60">{s.name}</div>
                  <h3 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-sinovar-navy">{s.tagline}</h3>
                  <p className="mt-4 max-w-lg text-base text-sinovar-navy/65 leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {t.services.bullets[s.key].map((b, bi) => (
                      <li key={bi} className="flex items-center gap-3 text-[15px] text-sinovar-navy/80">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${red ? "bg-sinovar-red/10 text-sinovar-red" : "bg-sinovar-blue/10 text-sinovar-blue"}`}>
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={0.1} className={flip ? "lg:order-1" : ""}>
                  <div className="overflow-hidden rounded-2xl shadow-xl shadow-sinovar-navy/10 aspect-[4/3]">
                    <img src={img} alt={s.name} className="h-full w-full object-cover" />
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </section>

      <SendLink />
      <Trust />
    </main>
  );
}
